<template>
  <layouts-header></layouts-header>
  <layouts-sidebar></layouts-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <div class="row">
        <div class="col-md-12">
          <!-- Page Header -->
          <page-header :title="title" />
          <!-- /Page Header -->

          <div class="row">
            <div class="col-xl-12 col-lg-12">
              <div class="card">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-5 col-sm-4">
                      <h4>Search by MPAN</h4>
                    </div>
                  </div>
                  <div class="settings-form">
                    <div class="profile-details">
                      <div class="row d-flex align-items-center">
                        <div class="col-md-10">
                          <div class="form-wrap">
                            <label class="col-form-label">MPAN Bottom Line <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="mpan" placeholder="Enter MPAN"
                              @keyup.enter="handleSearch" />
                          </div>
                        </div>
                        <div class="col-md-2 mt-2">
                          <button type="button" class="btn btn-primary" @click="handleSearch"
                            :disabled="loading || !mpan">
                            <span v-if="loading">Searching...</span>
                            <span v-else>Search</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- API Response Display -->
          <div class="row mt-4" v-if="apiResponse">
            <div class="col-xl-12 col-lg-12">
              <div class="card">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-12">
                      <h4>Search Results</h4>
                    </div>
                  </div>
                  <div class="row mt-3 border-top">
                    <div class="col-md-12">
                      <div class="table-responsive p-0">
                        <div class="mt-3">
                          <table class="table table-bordered">
                            <tbody>
                              <!-- Main fields (4 columns) -->
                              <tr v-for="(pair, index) in pairedFields" :key="index">
                                <td><strong>{{ formatLabel(pair[0].key) }}</strong></td>
                                <td>{{ pair[0].value }}</td>

                                <td v-if="pair[1]">
                                  <strong>{{ formatLabel(pair[1].key) }}</strong>
                                </td>
                                <td v-if="pair[1]">
                                  {{ pair[1].value }}
                                </td>

                                <td v-if="!pair[1]" colspan="2"></td>
                              </tr>

                              <!-- Additional details -->
                              <tr v-if="pairedAdditionalDetails.length">
                                <td colspan="4"><strong>Additional Details</strong></td>
                              </tr>

                              <tr v-for="(pair, index) in pairedAdditionalDetails" :key="'add-' + index">
                                <td><strong>{{ formatLabel(pair[0].key) }}</strong></td>
                                <td>{{ pair[0].value }}</td>

                                <td v-if="pair[1]">
                                  <strong>{{ formatLabel(pair[1].key) }}</strong>
                                </td>
                                <td v-if="pair[1]">
                                  {{ pair[1].value }}
                                </td>

                                <td v-if="!pair[1]" colspan="2"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div class="row mt-4" v-if="errorMessage">
            <div class="col-xl-12 col-lg-12">
              <div class="alert alert-danger" role="alert">
                {{ errorMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";

export default {
  data() {
    return {
      title: "ELECTRALINK",
    };
  },
  setup() {
    const store = useStore();
    const mpan = ref('');
    const loading = ref(false);
    const apiResponse = ref(null);
    const errorMessage = ref('');

    const handleSearch = async () => {
      if (!mpan.value || !mpan.value.trim()) {
        errorMessage.value = 'Please enter an MPAN';
        return;
      }

      loading.value = true;
      errorMessage.value = '';
      apiResponse.value = null;

      try {
        const paramsData = {
          search_value: mpan.value.trim(),
          type: 'ElectricLookup'
        };

        // Dispatch the Vuex action with paramsData
        const response = await store.dispatch(
          "price/fetchElectricLookupData",
          paramsData
        );

        if (response) {
          apiResponse.value = response;
        } else {
          errorMessage.value = 'No data found';
        }
      } catch (error) {
        console.error('Error fetching MPAN data:', error);
        errorMessage.value = error.response?.data?.message || 'An error occurred while fetching MPAN data';
      } finally {
        loading.value = false;
      }
    };

    // Function to format labels (replaces underscores with spaces and capitalizes)
    const formatLabel = (key) => {
      return key
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
        .replace(/_/g, " ") // Replace underscores with spaces
        .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
    };

    const pairArray = (arr) => {
      const result = [];
      for (let i = 0; i < arr.length; i += 2) {
        result.push([arr[i], arr[i + 1] || null]);
      }
      return result;
    };

    const pairedFields = computed(() => {
      if (!apiResponse.value) return [];

      const entries = Object.entries(apiResponse.value)
        .filter(([key, value]) =>
          key !== 'additional_detail' && typeof value !== 'object'
        )
        .map(([key, value]) => ({ key, value }));

      return pairArray(entries);
    });

    const pairedAdditionalDetails = computed(() => {
      const details = apiResponse.value?.additional_detail?.[0];
      if (!details) return [];

      const entries = Object.entries(details).map(([key, value]) => ({
        key,
        value
      }));

      return pairArray(entries);
    });


    return {
      mpan,
      loading,
      apiResponse,  
      errorMessage,
      handleSearch,
      formatLabel,
      pairedFields,
      pairedAdditionalDetails
    };
  },
};
</script>

<style scoped>
.settings-form {
  margin-top: 20px;
}

.form-wrap {
  margin-bottom: 20px;
}

.submit-button {
  margin-top: 20px;
}
</style>
